import { useEffect, useRef } from "react";

/**
 * Пиксельный «тоннель» — фон экранов входа. Квадраты медленно летят на
 * зрителя из глубины; центр остаётся пустым под карточку.
 *
 * Порт утверждённого `px.js` (`oneads-5.zip → handoff_px_animation`,
 * 14.09.2026). Все числа — из README «Approved settings», менять их нельзя:
 * узкий разброс скорости (0.90–1.12) и есть то, что делает движение
 * плавным, а не дёрганым; NEAR задаёт, насколько резко квадрат ускоряется,
 * проходя мимо зрителя.
 *
 * Что добавлено сверх скрипта, потому что там страница статическая, а
 * здесь SPA:
 * - отписка при размонтировании — иначе `requestAnimationFrame` крутился бы
 *   в отсоединённый канвас после ухода со страницы;
 * - `ResizeObserver` на родителе вместо `resize` окна — канвас тянется по
 *   родителю, а тот меняется и без окна;
 * - `prefers-reduced-motion` читается при монтировании, не при загрузке
 *   скрипта.
 */

const ACCENT: ReadonlyArray<readonly [number, number, number]> = [
  [230, 228, 255],
  [207, 203, 255],
  [141, 133, 255],
  [110, 98, 255],
  [90, 79, 240],
];

const FOV = 520;
const NEAR = 230;
const FAR = 1600;
const SPEED = 0.04;
const DENSITY = 3540;
const BASE_ALPHA = 0.5;

type Point = {
  x: number;
  y: number;
  z: number;
  c: readonly [number, number, number];
  s: number;
  a: number;
  sp: number;
};

export function PixelField({ cx = 0.5, cy = 0.5, fade = 0 }: { cx?: number; cy?: number; fade?: number }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      cv.style.display = "none";
      return;
    }
    const ctx = cv.getContext("2d");
    const host = cv.parentElement;
    if (!ctx || !host) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0;
    let H = 0;
    let pts: Point[] = [];
    let raf = 0;
    let prev = 0;

    const spawn = (p: Partial<Point>, z: number): Point => {
      // По кольцу вокруг центра, не по всей площади — тоннель остаётся
      // открытым за карточкой.
      const a = Math.random() * Math.PI * 2;
      const r = (0.42 + Math.random() * 0.85) * Math.max(W, H) * 0.5;
      p.x = Math.cos(a) * r;
      p.y = Math.sin(a) * r * 0.82;
      p.z = z;
      p.c = ACCENT[(Math.random() * ACCENT.length) | 0];
      p.s = 3.2 + Math.random() * 11;
      p.a = BASE_ALPHA + Math.random() * 0.4;
      p.sp = SPEED * (0.9 + Math.random() * 0.22);
      return p as Point;
    };

    const build = () => {
      const r = host.getBoundingClientRect();
      if (!r.width || !r.height) return;
      W = Math.round(r.width);
      H = Math.round(r.height);
      cv.style.width = `${W}px`;
      cv.style.height = `${H}px`;
      cv.width = W * dpr;
      cv.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const n = Math.max(16, Math.round((W * H) / DENSITY));
      pts = [];
      for (let i = 0; i < n; i++) pts.push(spawn({}, NEAR + Math.random() * (FAR - NEAR)));
    };

    const frame = (t: number) => {
      // Дельта зажата 28 мс: пропущенный кадр не даёт скачка.
      const dt = prev ? Math.min(28, t - prev) : 16;
      prev = t;
      if (W) {
        const ox = W * cx;
        const oy = H * cy;
        ctx.clearRect(0, 0, W, H);
        for (const p of pts) {
          p.z -= p.sp * dt;
          if (p.z < NEAR) {
            spawn(p, FAR - Math.random() * 120);
            continue;
          }
          const k = FOV / p.z;
          const x = ox + p.x * k;
          const y = oy + p.y * k;
          const sz = p.s * k;
          if (sz < 0.6 || x < -60 || y < -60 || x > W + 60 || y > H + 60) continue;
          // Появляется из глубины и гаснет, проносясь мимо зрителя.
          const fin = Math.min(1, (FAR - p.z) / 760);
          const fout = Math.min(1, (p.z - NEAR) / 700);
          let al = p.a * fin * fout;
          // Приглушение над колонкой текста, чтобы квадраты не спорили с заголовком.
          if (fade) {
            const fx = x / W;
            if (fx < fade) al *= 0.3 + 0.7 * Math.max(0, fx / fade);
          }
          if (al <= 0.012) continue;
          ctx.fillStyle = `rgba(${p.c[0]},${p.c[1]},${p.c[2]},${al.toFixed(3)})`;
          ctx.fillRect(x - sz / 2, y - sz / 2, sz, sz);
          if (sz > 13) {
            ctx.fillStyle = `rgba(255,255,255,${(al * 0.5).toFixed(3)})`;
            ctx.fillRect(x - sz / 6, y - sz / 6, sz / 3, sz / 3);
          }
        }
      }
      raf = requestAnimationFrame(frame);
    };

    build();
    raf = requestAnimationFrame(frame);

    let debounce = 0;
    const ro = new ResizeObserver(() => {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(build, 250);
    });
    ro.observe(host);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(debounce);
      ro.disconnect();
    };
  }, [cx, cy, fade]);

  return <canvas ref={ref} className="px-field" />;
}
